import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import type { EventWaiverItem } from '../../types';
import getEventWaivers from '../../hooks/waivers/getEventWaivers';
import { ChildHeaderRow, StatusChip, TaskStatus, WaiverActionButton } from './ToDoTableComponents';
import { WaiverAcceptModal } from './WaiverAcceptModal';

/** Unique key for a waiver row (child + setting). */
function getWaiverRowKey(w: EventWaiverItem): string {
  return `${w.childId}-${w.eventSetting.id}`;
}

/** Group waivers by child, sorted by child name. */
function groupWaiversByChild(waivers: EventWaiverItem[]) {
  const byChild = new Map<number, { childName: string; items: EventWaiverItem[] }>();
  for (const w of waivers) {
    const existing = byChild.get(w.childId);
    if (existing) {
      existing.items.push(w);
    } else {
      byChild.set(w.childId, { childName: w.childName, items: [w] });
    }
  }
  return Array.from(byChild.entries()).sort((a, b) =>
    a[1].childName.localeCompare(b[1].childName)
  );
}

export function WaiversSection() {
  const [waivers, setWaivers] = useState<EventWaiverItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalWaiver, setModalWaiver] = useState<EventWaiverItem | null>(null);
  const [modalReadOnly, setModalReadOnly] = useState(false);

  const fetchWaivers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEventWaivers();
      setWaivers(data);
    } catch (err) {
      console.error('Error loading waivers:', err);
      setError('We were unable to load waivers. Please try again later.');
      setWaivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWaivers();
  }, [fetchWaivers]);

  const openAcceptModal = (w: EventWaiverItem) => {
    setModalWaiver(w);
    setModalReadOnly(false);
  };

  const openViewModal = (w: EventWaiverItem) => {
    setModalWaiver(w);
    setModalReadOnly(true);
  };

  const closeModal = () => {
    setModalWaiver(null);
    setModalReadOnly(false);
  };

  const grouped = useMemo(() => groupWaiversByChild(waivers), [waivers]);

  if (loading) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#dff3f2',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Waivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign waivers once per child.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#dff3f2',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Waivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign waivers once per child.
          </Typography>
        </Box>
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={fetchWaivers} sx={{ textTransform: 'none' }}>
            Try again
          </Button>
        </Box>
      </Paper>
    );
  }

  if (waivers.length === 0) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#dff3f2',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Waivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign waivers once per child.
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Waiver</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 160 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No waivers required at this time.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#dff3f2',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Waivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign waivers once per child.
          </Typography>
        </Box>

        <Box
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Waiver</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 160 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map(([childId, { childName, items }]) => (
                <Fragment key={childId}>
                  <ChildHeaderRow name={childName} colSpan={4} />
                  {items.map((waiver) => {
                    const status: TaskStatus = waiver.completed ? 'Complete' : 'Incomplete';
                    return (
                      <TableRow key={getWaiverRowKey(waiver)} hover>
                        <TableCell>{waiver.templateName}</TableCell>
                        <TableCell>
                          <StatusChip status={status} />
                        </TableCell>
                        <TableCell>
                          {waiver.completed && waiver.completedAt
                            ? dayjs(waiver.completedAt).format('MM/DD/YYYY')
                            : waiver.completed
                              ? 'Signed'
                              : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <WaiverActionButton
                            status={status}
                            onComplete={waiver.completed ? undefined : () => openAcceptModal(waiver)}
                            onView={waiver.completed ? () => openViewModal(waiver) : undefined}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <WaiverAcceptModal
        open={modalWaiver !== null}
        waiver={modalWaiver}
        onClose={closeModal}
        onAccepted={fetchWaivers}
        readOnly={modalReadOnly}
      />
    </>
  );
}
